import CustomTooltip from '../../shared/CustomTooltip';
import {
    Box,
    Grid,
    List,
    ListItem,
    Paper,
    Typography,
    Collapse,
    IconButton,
    Stack,
    useMediaQuery,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import React, { useState } from 'react';
import moment from 'moment-timezone';
import ProfilePicture from '../Profile/ProfilePicture';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CustomNoRowsOverlay from '../../shared/NoRowsOverlayCustom';
import UserRole from '../../../shared/AuthRoles';
import { Status as BookingStatus } from '../../enums/booking';
import { Status as JobStatus } from '../../enums/contractor';
import { CustomChip } from '../../../layout/styledcomponents/CustomChip';
import { theme } from '../../../shared/theme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { styled } from '@mui/material/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type AccessResult = { ok: true } | { ok: false; error: string };

const headerTypographySx = {
    fontWeight: 700,
    letterSpacing: 0.2,
};

const CleanDataGrid = (props: any) => (
    <DataGrid
        {...props}
        sx={{
            fontFamily: ['Inter'].join(','),
            border: 0,
            boxShadow: 0,
            '& .MuiDataGrid-columnHeader': {
                borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                backgroundColor: theme.palette.neutral.main,
            },
            '& .MuiDataGrid-columnHeaders': {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                overflow: 'hidden',
                position: 'sticky',
                top: 0,
                zIndex: 2,
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
                paddingLeft: 0,
                paddingRight: 0,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
                width: '100%',
                fontWeight: 800,
                fontSize: 12,
            },
            '& .MuiDataGrid-row': {
                borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
            },
            '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.custom.cardActionBackground,
            },
            '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                lineHeight: '1.4rem',
                padding: '10px 8px',
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
            },
            '& .MuiDataGrid-virtualScroller': {
                overflowX: 'hidden',
            },
            '& .MuiDataGrid-main': { overflowX: 'hidden' },
            '& .MuiDataGrid-footerContainer': {
                borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
                justifyContent: 'flex-end',
                minHeight: 52,
            },
            '& .MuiTablePagination-root': {
                width: 'auto',
            },
            '& .MuiTablePagination-toolbar': {
                width: 'auto',
                paddingLeft: 0,
                paddingRight: 0,
                minHeight: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0,
            },
            '& .MuiTablePagination-spacer': {
                display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
                margin: 0,
                marginRight: 0,
                whiteSpace: 'nowrap',
            },
            '& .MuiTablePagination-input': {
                margin: 0,
                marginRight: 10,
            },
            '& .MuiTablePagination-displayedRows': {
                margin: 0,
                marginLeft: 0,
                marginRight: 10,
                whiteSpace: 'nowrap',
            },
            '& .MuiTablePagination-actions': {
                marginLeft: 0,
            },
            ...(props?.sx || {}),
        }}
    />
);

const WhiteTooltip = styled(({ className, ...props }: any) => (
    <CustomTooltip {...props} classes={{ popper: className }} arrow />
))(({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
        backgroundColor: theme.palette.neutral.main,
        color: theme.palette.primary.main,
        fontSize: 12,
        fontWeight: 600,
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        padding: '8px 12px',
    },
    [`& .MuiTooltip-arrow`]: {
        color: theme.palette.neutral.main,
    },
}));

const NirovisionAccessTable = ({
    data,
    status,
    start,
    end,
    usesVehicleRec,
    accessType,
    bookerType,
    provideAccess,
    removeAccess,
    forceMobile,
}: {
    data: any;
    status: BookingStatus | JobStatus;
    start: string;
    end: string;
    usesVehicleRec: boolean;
    accessType: 'job' | 'booking';
    bookerType?: string;
    provideAccess: (id: number) => Promise<AccessResult>;
    removeAccess: (id: number) => Promise<AccessResult>;
    forceMobile?: boolean;
}) => {
    const loggedInUser = UserRole();
    const mqMobile = useMediaQuery('(max-width: 770px)');
    const isMobile = typeof forceMobile === 'boolean' ? forceMobile : mqMobile;

    const tz = 'Australia/Sydney';
    const now = moment.tz(tz);

    const startDate = moment.tz(start, tz).startOf('day');
    const endDate = moment.tz(end, tz).endOf('day');

    const accessWindowStart = startDate
        .clone()
        .subtract(1, 'day')
        .hour(15)
        .minute(0)
        .second(0)
        .millisecond(0);

    const isCurrentDateInRange = now.isBetween(
        accessWindowStart,
        endDate,
        undefined,
        '[]',
    );

    const [addSubmittingRows, setAddSubmittingRows] = useState<
        Record<string, boolean>
    >({});
    const [removeSubmittingRows, setRemoveSubmittingRows] = useState<
        Record<string, boolean>
    >({});
    const [invalidPhotoByUserId, setInvalidPhotoByUserId] = useState<
        Record<number, string>
    >({});
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});

    const toggleExpandedRow = (rowId: number) => {
        setExpanded((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    };

    const csvEscape = (v: any) => {
        const s = String(v ?? '');
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    };

    const formatDate = (d: any) => {
        if (!d) return '';
        const m = moment(d);
        return m.isValid() ? m.format('D MMM YYYY') : '';
    };

    const getAccessLabel = (
        row: any,
        type: 'face' | 'vehicle',
    ): 'Granted' | 'Not granted' | 'Revoked' => {
        const removedAt =
            type === 'face' ? row?.faceRecRemovedAt : row?.vehicleRemovedAt;
        if (removedAt) return 'Revoked';

        const providedAt =
            type === 'face' ? row?.faceRecProvidedAt : row?.vehicleProvidedAt;

        return providedAt ? 'Granted' : 'Not granted';
    };

    const buildExportRows = (
        rows: any[],
        group: 'With access' | 'Without access',
    ) =>
        (rows || []).map((row) => {
            const name =
                `${row?.firstName ?? ''} ${row?.lastName ?? ''}`.trim() ||
                'External Guest';

            return {
                group,
                name,
                email: row?.email ?? '',
                vehiclePlate: row?.vehiclePlate ?? '',
                faceAccess: getAccessLabel(row, 'face'),
                vehicleAccess: usesVehicleRec
                    ? getAccessLabel(row, 'vehicle')
                    : '',
                faceGrantedAt: formatDate(row?.faceRecProvidedAt),
                faceRevokedAt: formatDate(row?.faceRecRemovedAt),
                vehicleGrantedAt: usesVehicleRec
                    ? formatDate(row?.vehicleProvidedAt)
                    : '',
                vehicleRevokedAt: usesVehicleRec
                    ? formatDate(row?.vehicleRemovedAt)
                    : '',
                isActive: row?.isActive === false ? 'No' : 'Yes',
            };
        });

    const downloadCsv = (filename: string, rows: Record<string, any>[]) => {
        const headers = [
            'Group',
            'Name',
            'Email',
            'Vehicle Plate',
            'Face Access',
            'Vehicle Access',
            'Face Granted At',
            'Face Revoked At',
            'Vehicle Granted At',
            'Vehicle Revoked At',
            'Active',
        ];

        const lines = [
            headers.join(','),
            ...rows.map((r) =>
                [
                    r.group,
                    r.name,
                    r.email,
                    r.vehiclePlate,
                    r.faceAccess,
                    r.vehicleAccess,
                    r.faceGrantedAt,
                    r.faceRevokedAt,
                    r.vehicleGrantedAt,
                    r.vehicleRevokedAt,
                    r.isActive,
                ]
                    .map(csvEscape)
                    .join(','),
            ),
        ];

        const blob = new Blob([lines.join('\n')], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        URL.revokeObjectURL(url);
    };

    const exportCsv = () => {
        const withRows = Array.isArray(data?.faceRecProvidedTo)
            ? data.faceRecProvidedTo
            : [];
        const withoutRows = Array.isArray(data?.faceRecNotProvidedTo)
            ? data.faceRecNotProvidedTo
            : [];

        const exportRows = [
            ...buildExportRows(withRows, 'With access'),
            ...buildExportRows(withoutRows, 'Without access'),
        ];

        const from = moment(start).format('YYYYMMDD');
        const to = moment(end).format('YYYYMMDD');
        const filename = `nirovision_access_${accessType}_${from}_${to}.csv`;

        downloadCsv(filename, exportRows);
    };

    const isDisabled =
        !isCurrentDateInRange ||
        ['cancelled', 'pending', 'completed'].includes(String(status));

    const isVehicleMissing = (row: any) =>
        usesVehicleRec && String(row?.vehiclePlate ?? '').trim()?.length === 0;

    const getStatusMessage = (row: any) => {
        if (!row?.isActive) return 'User is deactivated';

        const cached = invalidPhotoByUserId[row?.id];
        if (cached) return cached;

        const pic = row?.profilePicture;
        if (!pic?.id) return 'Profile photo required';

        const invalidFlag =
            pic?.isValid === false ||
            pic?.status === 'invalid' ||
            pic?.status === 'rejected';

        if (invalidFlag) return 'Photo needs review';

        const createdAt = pic?.createdAt || pic?.created || pic?.updatedAt;
        if (createdAt) {
            const picTime = moment(createdAt);
            if (picTime.isValid() && now.diff(picTime, 'hours') < 24) {
                return 'Photo recently updated (may take a moment to sync)';
            }
        }

        return null;
    };

    const slugify = (v: any) =>
        String(v ?? '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

    const getUserProfileHref = (row: any) => {
        const firstName = slugify(row?.firstName || 'user');
        const id = row?.id ?? '';
        return `/users/${firstName}/${id}`;
    };

    const openUserProfile = (row: any) => {
        const href = getUserProfileHref(row);
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const handleApiCall = async (
        mutation: (id: number) => Promise<AccessResult>,
        userId: number,
        setSubmitting: React.Dispatch<
            React.SetStateAction<Record<string, boolean>>
        >,
    ) => {
        const key = String(userId);
        setSubmitting((prev) => ({ ...prev, [key]: true }));

        const res = await mutation(userId);

        if (res.ok) {
            setInvalidPhotoByUserId((prev) => {
                const next = { ...prev };
                delete next[userId];
                return next;
            });
        } else {
            if (
                String(res.error).includes(
                    'Missing or invalid profile picture',
                ) ||
                String(res.error).includes('invalid profile picture')
            ) {
                setInvalidPhotoByUserId((prev) => ({
                    ...prev,
                    [userId]: 'Missing or invalid profile picture',
                }));
            }
        }

        setSubmitting((prev) => ({ ...prev, [key]: false }));
        return res;
    };

    const renderDetailsList = (row: any) => {
        const info = getStatusMessage(row);

        const faceProvided =
            row?.faceRecProvidedAt !== null && !row?.faceRecRemovedAt;
        const faceRevoked = Boolean(row?.faceRecRemovedAt);

        const vehicleProvided =
            row?.vehicleProvidedAt !== null && !row?.vehicleRemovedAt;
        const vehicleRevoked = Boolean(row?.vehicleRemovedAt);

        return (
            <List
                disablePadding
                sx={{
                    listStyleType: 'disc',
                    pl: 2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    fontSize: '12px',
                    '& .MuiListItem-root': {
                        fontSize: '12px',
                    },
                }}
            >
                {info ? (
                    <ListItem sx={{ padding: 0, display: 'list-item' }}>
                        {info}
                    </ListItem>
                ) : null}

                <ListItem sx={{ padding: 0, display: 'list-item' }}>
                    Face:{' '}
                    {faceRevoked
                        ? `Revoked (${moment(row.faceRecRemovedAt).format('D MMM YYYY')})`
                        : faceProvided
                          ? `Granted (${moment(row.faceRecProvidedAt).format('D MMM YYYY')})`
                          : 'Not granted'}
                </ListItem>

                {usesVehicleRec ? (
                    <ListItem sx={{ padding: 0, display: 'list-item' }}>
                        Vehicle:{' '}
                        {vehicleRevoked
                            ? `Revoked (${moment(row.vehicleRemovedAt).format('D MMM YYYY')})`
                            : vehicleProvided
                              ? `Granted (${moment(row.vehicleProvidedAt).format('D MMM YYYY')})`
                              : 'Not granted'}
                    </ListItem>
                ) : null}

                {isVehicleMissing(row) ? (
                    <ListItem sx={{ padding: 0, display: 'list-item' }}>
                        Rego not provided (granting access will apply to Face
                        only)
                    </ListItem>
                ) : null}

                {String(status) === 'pending' ? (
                    <ListItem sx={{ padding: 0, display: 'list-item' }}>
                        Access is available once this {accessType} is approved,
                        and from 3pm the day before check-in.
                    </ListItem>
                ) : now.isBefore(accessWindowStart) ? (
                    <ListItem sx={{ padding: 0, display: 'list-item' }}>
                        Access starts{' '}
                        {accessWindowStart.format('D MMM, h:mm A')}
                    </ListItem>
                ) : null}
            </List>
        );
    };

    const MobileSectionHeader = ({
        title,
        showExport,
    }: {
        title: string;
        showExport?: boolean;
    }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                mb: 1,
                gap: 1,
            }}
        >
            {showExport ? (
                <WhiteTooltip title='Download full access list including face and vehicle access'>
                    <Button
                        variant='text'
                        onClick={exportCsv}
                        startIcon={
                            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                        }
                        sx={{
                            textTransform: 'none',
                            fontWeight: 900,
                            fontSize: 12,
                            borderRadius: 2,
                            px: 1,
                            minHeight: 30,
                            '&:hover': {
                                backgroundColor:
                                    theme.palette.custom.cardActionBackground,
                            },
                        }}
                    >
                        Export
                    </Button>
                </WhiteTooltip>
            ) : null}
        </Box>
    );

    const canGiveAccess = (row: any) => {
        const userId = Number(row?.id);
        const key = String(userId);

        const isRowSubmitting = addSubmittingRows[key] || false;
        const isProfilePic = Boolean(row?.profilePicture?.id);
        const hasInvalidPhoto = Boolean(invalidPhotoByUserId[userId]);

        const roleBlocked =
            (loggedInUser.role.includes('owner') && bookerType !== 'owner') ||
            (loggedInUser.role.includes('user') &&
                loggedInUser?.sub !== row?.id);

        const disabled =
            isDisabled ||
            !isProfilePic ||
            hasInvalidPhoto ||
            isRowSubmitting ||
            !row?.isActive ||
            roleBlocked;

        return !disabled;
    };

    const MobileUserCard = ({
        row,
        mode,
    }: {
        row: any;
        mode: 'with' | 'without';
    }) => {
        const userId = Number(row?.id);
        const key = String(userId);

        const isRemove = mode === 'with';

        const isRowSubmitting =
            (isRemove ? removeSubmittingRows : addSubmittingRows)[key] || false;

        const isProfilePic = Boolean(row?.profilePicture?.id);
        const hasInvalidPhoto = Boolean(invalidPhotoByUserId[userId]);

        const roleBlocked =
            (loggedInUser.role.includes('owner') && bookerType !== 'owner') ||
            (loggedInUser.role.includes('user') &&
                loggedInUser?.sub !== row?.id);

        const disabled = isRemove
            ? row?.faceRecRemovedAt || roleBlocked
            : isDisabled ||
              !isProfilePic ||
              hasInvalidPhoto ||
              isRowSubmitting ||
              !row?.isActive ||
              roleBlocked;

        const isExpanded = Boolean(expanded[userId]);

        const helper =
            mode === 'with'
                ? 'View details'
                : !row?.isActive
                  ? 'User is deactivated.'
                  : 'Access not available yet. View details.';

        const showAlert = mode === 'with' || (disabled && Boolean(helper));

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    backgroundColor: theme.palette.neutral.main,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: 1.25 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.25,
                            alignItems: 'flex-start',
                        }}
                    >
                        <ProfilePicture
                            profileDetails={row}
                            src={row?.profilePicture?.src}
                            sx={{ width: 42, height: 42, flexShrink: 0 }}
                        />

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        flex: 1,
                                        cursor: 'pointer',
                                        '&:active .name': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                    onClick={() => openUserProfile(row)}
                                    role='link'
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            e.preventDefault();
                                            openUserProfile(row);
                                        }
                                    }}
                                >
                                    <Typography
                                        className='name'
                                        sx={{
                                            fontWeight: 950,
                                            fontSize: 14,
                                            lineHeight: 1.15,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {row?.firstName
                                            ? `${row.firstName} ${row?.lastName ?? ''}`.trim()
                                            : 'External Guest'}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            mt: 0.25,
                                        }}
                                    >
                                        {row?.email}
                                    </Typography>
                                </Box>

                                <Stack
                                    direction='row'
                                    spacing={0.25}
                                    alignItems='center'
                                >
                                    <WhiteTooltip title='View profile'>
                                        <IconButton
                                            onClick={() => openUserProfile(row)}
                                            sx={{
                                                p: 0.5,
                                                borderRadius: 2,
                                                backgroundColor: 'transparent',
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.custom
                                                            .cardActionBackground,
                                                },
                                            }}
                                        >
                                            <OpenInNewIcon
                                                sx={{ fontSize: 20 }}
                                            />
                                        </IconButton>
                                    </WhiteTooltip>
                                </Stack>
                            </Box>

                            {showAlert ? (
                                <Alert
                                    severity='info'
                                    icon={false}
                                    onClick={() => toggleExpandedRow(userId)}
                                    sx={{
                                        mt: 0.9,
                                        borderRadius: 2,
                                        fontSize: 10,
                                        py: 0.35,
                                        px: 0.75,
                                        minHeight: 0,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor:
                                            theme.palette.custom
                                                .cardActionBackground,
                                        color: theme.palette.text.primary,
                                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                        '& .MuiAlert-message': {
                                            py: 0,
                                            lineHeight: 1.2,
                                            width: '100%',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            width: '100%',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                minWidth: 0,
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: theme.palette.primary
                                                        .main,
                                                    opacity: 0.9,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box
                                                component='span'
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {helper}
                                            </Box>
                                        </Box>

                                        <ExpandMoreIcon
                                            sx={{
                                                fontSize: 18,
                                                transition:
                                                    'transform 140ms ease',
                                                transform: isExpanded
                                                    ? 'rotate(180deg)'
                                                    : 'rotate(0deg)',
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Box>
                                </Alert>
                            ) : null}

                            <Collapse in={isExpanded} timeout={140}>
                                <Box sx={{ mt: 1.1 }}>
                                    {renderDetailsList(row)}
                                </Box>
                            </Collapse>

                            {row?.isActive && (
                                <Box sx={{ mt: 1.1 }}>
                                    <Button
                                        fullWidth
                                        size='small'
                                        variant={
                                            isRemove ? 'outlined' : 'contained'
                                        }
                                        color='primary'
                                        disabled={disabled}
                                        onClick={async () => {
                                            await handleApiCall(
                                                isRemove
                                                    ? removeAccess
                                                    : provideAccess,
                                                userId,
                                                isRemove
                                                    ? setRemoveSubmittingRows
                                                    : setAddSubmittingRows,
                                            );
                                        }}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            boxShadow: 'none',
                                            height: 32,
                                            fontSize: 12,
                                            fontWeight: 700,
                                            ...(disabled
                                                ? { opacity: 0.7 }
                                                : {}),
                                        }}
                                    >
                                        {isRowSubmitting ? (
                                            <CircularProgress
                                                size={18}
                                                sx={{ color: 'inherit' }}
                                            />
                                        ) : isRemove ? (
                                            'Remove access'
                                        ) : (
                                            'Give access'
                                        )}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>
        );
    };

    if (isMobile) {
        const withRows = Array.isArray(data?.faceRecProvidedTo)
            ? data.faceRecProvidedTo
            : [];

        const withoutRowsRaw = Array.isArray(data?.faceRecNotProvidedTo)
            ? data.faceRecNotProvidedTo
            : [];

        const withoutRows = [...withoutRowsRaw].sort((a, b) => {
            const aCan = canGiveAccess(a);
            const bCan = canGiveAccess(b);
            if (aCan === bCan) return 0;
            return aCan ? -1 : 1;
        });

        const showExportInWith = Boolean(withRows?.length);
        const showExportInWithout =
            !showExportInWith && Boolean(withoutRows?.length);

        return (
            <Stack spacing={1.25}>
                {withRows?.length ? (
                    <Box>
                        <MobileSectionHeader
                            title='Users with access'
                            showExport={showExportInWith}
                        />
                        <Stack spacing={1.25}>
                            {withRows.map((row: any) => (
                                <MobileUserCard
                                    key={row?.id}
                                    row={row}
                                    mode='with'
                                />
                            ))}
                        </Stack>
                    </Box>
                ) : null}

                {withoutRows?.length ? (
                    <Box sx={{ mt: withRows?.length ? 1.25 : 0 }}>
                        <MobileSectionHeader
                            title='Users without access'
                            showExport={showExportInWithout}
                        />
                        <Stack spacing={1.25}>
                            {withoutRows.map((row: any) => (
                                <MobileUserCard
                                    key={row?.id}
                                    row={row}
                                    mode='without'
                                />
                            ))}
                        </Stack>
                    </Box>
                ) : null}

                {!withRows?.length && !withoutRows?.length ? (
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            backgroundColor: theme.palette.neutral.main,
                            p: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 13,
                            }}
                        >
                            No users found.
                        </Typography>
                    </Paper>
                ) : null}
            </Stack>
        );
    }

    const HeaderCell = ({
        label,
        align,
    }: {
        label: string;
        align?: 'left' | 'center' | 'right';
    }) => (
        <Typography
            component='h6'
            variant='body3'
            sx={{
                ...headerTypographySx,
                width: '100%',
                textAlign: align || 'left',
            }}
        >
            {label}
        </Typography>
    );

    const GuestCell = (params: any) => {
        const href = getUserProfileHref(params.row);

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <Box
                    role='link'
                    tabIndex={0}
                    onClick={() => openUserProfile(params.row)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openUserProfile(params.row);
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                        cursor: 'pointer',
                        '&:hover .name': { textDecoration: 'underline' },
                    }}
                >
                    <ProfilePicture
                        profileDetails={params.row}
                        src={params.row?.profilePicture?.src}
                        sx={{ width: 40, height: 40, flexShrink: 0 }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            className='name'
                            sx={{
                                fontWeight: 800,
                                fontSize: 13,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {params.row?.firstName
                                ? `${params.row.firstName} ${params.row?.lastName ?? ''}`.trim()
                                : 'External Guest'}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {params.row?.email}
                        </Typography>
                    </Box>
                </Box>

                <WhiteTooltip title='View profile'>
                    <IconButton
                        size='small'
                        onClick={() =>
                            window.open(href, '_blank', 'noopener,noreferrer')
                        }
                        sx={{ flexShrink: 0 }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </WhiteTooltip>
            </Box>
        );
    };

    const AccessStatusCell = (params: any, type: 'vehicle' | 'face') => {
        const provided =
            type === 'vehicle'
                ? Boolean(params.row.vehicleProvidedAt) &&
                  !params.row.vehicleRemovedAt
                : Boolean(params.row.faceRecProvidedAt) &&
                  !params.row.faceRecRemovedAt;

        const revoked =
            type === 'vehicle'
                ? Boolean(params.row.vehicleRemovedAt)
                : Boolean(params.row.faceRecRemovedAt);

        const label = revoked
            ? 'Revoked'
            : provided
              ? 'Granted'
              : 'Not granted';
        const color = revoked ? 'yellow' : provided ? 'green' : 'red';

        const icon = revoked ? (
            <BlockOutlinedIcon sx={{ fontSize: 16 }} />
        ) : provided ? (
            <CheckCircleIcon sx={{ fontSize: 16 }} />
        ) : (
            <CancelIcon sx={{ fontSize: 16 }} />
        );

        return (
            <CustomChip
                label={
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                        {icon}
                        <Typography
                            variant='body3'
                            sx={{ fontSize: 11, fontWeight: 800 }}
                        >
                            {label}
                        </Typography>
                    </Stack>
                }
                color={color}
                variant='outlined'
                sx={{
                    height: 32,
                    px: 1.25,
                    '& .MuiChip-label': { px: 0 },
                }}
            />
        );
    };

    const ActionButtonCell = (params: any, isRemove: boolean) => {
        const isRowSubmitting =
            (isRemove ? removeSubmittingRows : addSubmittingRows)[params.id] ||
            false;

        const roleBlocked =
            (loggedInUser.role.includes('owner') && bookerType !== 'owner') ||
            (loggedInUser.role.includes('user') &&
                loggedInUser?.sub !== params.row?.id);

        const isProfilePic = Boolean(params.row?.profilePicture?.id);
        const hasInvalidPhoto = Boolean(invalidPhotoByUserId[params.row?.id]);

        const disabled = isRemove
            ? params.row.faceRecRemovedAt || roleBlocked
            : isDisabled ||
              !isProfilePic ||
              hasInvalidPhoto ||
              isRowSubmitting ||
              !params.row?.isActive ||
              roleBlocked;

        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Button
                    variant={isRemove ? 'outlined' : 'contained'}
                    color='primary'
                    disabled={disabled}
                    onClick={async () => {
                        await handleApiCall(
                            isRemove ? removeAccess : provideAccess,
                            Number(params.row.id),
                            isRemove
                                ? setRemoveSubmittingRows
                                : setAddSubmittingRows,
                        );
                    }}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        height: 34,
                        minWidth: 150,
                    }}
                >
                    {isRowSubmitting ? (
                        <CircularProgress size={18} sx={{ color: 'inherit' }} />
                    ) : isRemove ? (
                        'Remove access'
                    ) : (
                        'Give access'
                    )}
                </Button>
            </Box>
        );
    };

    const DetailsWrap = (children: React.ReactNode) => (
        <Box
            sx={{
                width: '100%',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                overflow: 'visible',
            }}
        >
            {children}
        </Box>
    );

    const DetailsToggleCell = (params: any) => {
        const rowId = Number(params.row?.id);
        const isOpen = Boolean(expanded[rowId]);

        return (
            <Box sx={{ width: '100%' }}>
                <Button
                    variant='text'
                    size='small'
                    onClick={() => toggleExpandedRow(rowId)}
                    endIcon={
                        <ExpandMoreIcon
                            sx={{
                                fontSize: 18,
                                transition: 'transform 140ms ease',
                                transform: isOpen
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)',
                            }}
                        />
                    }
                    sx={{
                        textTransform: 'none',

                        fontSize: 12,
                        borderRadius: 2,
                        px: 1,
                        minHeight: 30,
                        justifyContent: 'flex-start',
                    }}
                >
                    {isOpen ? 'Hide details' : 'Show details'}
                </Button>

                <Collapse in={isOpen} timeout={140}>
                    <Box sx={{ mt: 1 }}>
                        {DetailsWrap(renderDetailsList(params.row))}
                    </Box>
                </Collapse>
            </Box>
        );
    };

    const columnsProvidedTo: GridColDef[] = [
        {
            field: 'firstName',
            headerName: accessType === 'job' ? 'Contractor' : 'Guest',
            flex: 1.4,
            minWidth: 220,
            renderHeader: () => (
                <HeaderCell
                    label={accessType === 'job' ? 'Contractor' : 'Guest'}
                />
            ),
            renderCell: GuestCell,
        },
        ...(usesVehicleRec
            ? [
                  {
                      field: 'vehiclePlate',
                      headerName: 'Vehicle Plate',
                      flex: 0.9,
                      minWidth: 150,
                      align: 'center',
                      headerAlign: 'center',
                      disableColumnMenu: true,
                      sortable: false,
                      renderHeader: () => (
                          <HeaderCell label='Vehicle Plate' align='center' />
                      ),
                      renderCell: (params: any) =>
                          loggedInUser.role.includes('guest_user') &&
                          loggedInUser?.sub !== params.row?.id ? (
                              ''
                          ) : (
                              <CustomChip
                                  label={
                                      <Typography
                                          variant='body3'
                                          sx={{ fontSize: 11, fontWeight: 800 }}
                                      >
                                          {params.value || 'No rego details'}
                                      </Typography>
                                  }
                                  color='primary'
                                  variant='outlined'
                                  sx={{
                                      height: 28,
                                      px: 1.25,
                                      minWidth: 118,
                                      justifyContent: 'center',
                                      '& .MuiChip-label': { px: 0 },
                                  }}
                              />
                          ),
                  } as GridColDef,
                  {
                      field: 'vehicleProvidedAt',
                      headerName: 'Vehicle Rec Access',
                      flex: 0.8,
                      minWidth: 140,
                      align: 'center',
                      headerAlign: 'center',
                      disableColumnMenu: true,
                      sortable: false,
                      renderHeader: () => (
                          <HeaderCell
                              label='Vehicle Rec Access'
                              align='center'
                          />
                      ),
                      renderCell: (params: any) =>
                          AccessStatusCell(params, 'vehicle'),
                  } as GridColDef,
              ]
            : []),
        {
            field: 'isActive',
            headerName: 'Face Rec Access',
            flex: 0.8,
            minWidth: 140,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => (
                <HeaderCell label='Face Rec Access' align='center' />
            ),
            renderCell: (params: any) => AccessStatusCell(params, 'face'),
        },
        {
            field: 'details',
            headerName: '',
            flex: 1.2,
            minWidth: 200,
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => <HeaderCell label='' />,
            valueGetter: () => '',
            cellClassName: 'detailsCell',
            renderCell: DetailsToggleCell,
        },
        {
            field: 'id',
            headerName: 'Actions',
            flex: 0.7,
            minWidth: 150,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => <HeaderCell label='Actions' align='center' />,
            renderCell: (params: any) => ActionButtonCell(params, true),
        },
    ];

    const columnsNotProvidedTo: GridColDef[] = [
        ...columnsProvidedTo.slice(0, columnsProvidedTo.length - 2),
        {
            field: 'details2',
            headerName: '',
            flex: 1.2,
            minWidth: 200,
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => <HeaderCell label='' />,
            valueGetter: () => '',
            cellClassName: 'detailsCell',
            renderCell: DetailsToggleCell,
        },
        {
            field: 'id',
            headerName: 'Actions',
            flex: 0.7,
            minWidth: 150,
            align: 'center',
            headerAlign: 'center',
            disableColumnMenu: true,
            sortable: false,
            renderHeader: () => <HeaderCell label='Actions' align='center' />,
            renderCell: (params: any) => ActionButtonCell(params, false),
        },
    ];

    const gridCommonSx = {
        width: '100%',
        '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.4rem',
            padding: '10px 8px',
            overflow: 'visible',
            alignItems: 'flex-start',
            py: 1,
        },
        '& .detailsCell': {
            overflow: 'visible',
        },
    };

    return (
        <>
            {data?.faceRecProvidedTo?.length > 0 && (
                <Paper
                    elevation={0}
                    square
                    sx={{
                        backgroundColor: theme.palette.neutral.main,
                        p: { xs: 2, sm: 3, md: 3.5 },
                        boxShadow: 'none',
                        mb: 2,
                    }}
                >
                    <Grid item xs={12}>
                        <CleanDataGrid
                            getRowHeight={() => 'auto'}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                            rows={data?.faceRecProvidedTo || []}
                            columns={columnsProvidedTo}
                            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                            sx={gridCommonSx}
                        />
                    </Grid>
                </Paper>
            )}

            {data?.faceRecNotProvidedTo?.length > 0 && (
                <Paper
                    elevation={0}
                    square
                    sx={{
                        backgroundColor: theme.palette.neutral.main,
                        p: { xs: 2, sm: 3, md: 3.5 },
                        boxShadow: 'none',
                        mb: 2,
                    }}
                >
                    <Grid item xs={12}>
                        <CleanDataGrid
                            getRowHeight={() => 'auto'}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                            rows={data?.faceRecNotProvidedTo || []}
                            columns={columnsNotProvidedTo}
                            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                            sx={gridCommonSx}
                        />
                    </Grid>
                </Paper>
            )}
        </>
    );
};

export default NirovisionAccessTable;
